import React from 'react';
import { Plus } from 'lucide-react';
import { getButtonClasses } from '../../../../hooks/useMobileUtils';
import { EntryIcon } from './EntryIcon';
import { quickAddTypes, isSupervisoryVisit } from '../model/entryMeta';
import { noteTemplateFor } from '../model/noteTemplates';
import type { Entry } from '../types';

export const QUICK_ADD_LABELS: Record<string, string> = {
  vitals: 'Vitals',
  mar: 'Meds',
  respiratory: 'Resp',
  seizure: 'Seizure',
  feed: 'Feed',
  output: 'Output',
  notes: 'Note',
  wound: 'Wound',
  incident: 'Incident',
  summary: 'Summary',
  measurements: 'Measurements',
  supervisory: 'Supervisory note',
  therapy: 'Treatment',
  aidetasks: 'Tasks',
  symptom: 'Symptoms',
};

export const QUICK_ADD_TONES: Record<string, string> = {
  vitals: 'bg-blue-50 text-blue-700 border-blue-400 hover:bg-blue-100',
  mar: 'bg-green-50 text-green-700 border-green-400 hover:bg-green-100',
  respiratory: 'bg-cyan-50 text-cyan-700 border-cyan-400 hover:bg-cyan-100',
  seizure: 'bg-orange-50 text-orange-700 border-orange-400 hover:bg-orange-100',
  feed: 'bg-amber-50 text-amber-700 border-amber-400 hover:bg-amber-100',
  output: 'bg-lime-50 text-lime-700 border-lime-400 hover:bg-lime-100',
  notes: 'bg-purple-50 text-purple-700 border-purple-400 hover:bg-purple-100',
  wound: 'bg-pink-50 text-pink-700 border-pink-400 hover:bg-pink-100',
  incident: 'bg-red-50 text-red-700 border-red-400 hover:bg-red-100',
  summary: 'bg-slate-50 text-slate-700 border-slate-400 hover:bg-slate-100',
  measurements: 'bg-yellow-50 text-yellow-700 border-yellow-400 hover:bg-yellow-100',
  supervisory: 'bg-teal-50 text-teal-700 border-teal-400 hover:bg-teal-100',
  therapy: 'bg-emerald-50 text-emerald-700 border-emerald-400 hover:bg-emerald-100',
  aidetasks: 'bg-lime-50 text-lime-800 border-lime-500 hover:bg-lime-100',
};

/**
 * Phase 18 M8 quick-add: nursing Vitals, Meds, Resp, Seizure, Feed, Output, Note, Wound, Incident,
 * Summary, Measurements; therapy Note, Vitals, Summary, Measurements, Incident. F21: a supervisory
 * visit adds "Supervisory note" first and a hint until one is charted.
 */
const QuickAddMenu: React.FC<{
  onAdd: (type: string) => void;
  disabled: boolean;
  isMobile: boolean;
  discipline?: string | null;
  visitType?: string | null;
  entries?: Entry[];
  /** Phase 20F: the patient is in the palliative care programme (adds the symptom entry) */
  palliative?: boolean;
}> = ({ onAdd, disabled, isMobile, discipline, visitType, entries = [], palliative = false }) => {
  const types = quickAddTypes(discipline, visitType, { palliative });
  const needsSupervisoryNote = isSupervisoryVisit(visitType) && !entries.some((e) => e.type === 'supervisory' && !e.invalidated);
  return (
    <div className={`pb-2 ${isMobile ? 'border-b border-gray-200 mb-4' : ''}`}>
      {needsSupervisoryNote && (
        <p className="mb-2 rounded border border-teal-300 bg-teal-50 px-2 py-1 text-xs text-teal-900" data-testid="supervisory-note-hint">
          Supervisory visit: add the supervisory note before you sign and submit the chart.
        </p>
      )}
      <div className={`flex flex-wrap ${isMobile ? 'gap-2' : 'gap-2 md:gap-3'}`} data-testid="quick-add-menu">
        {types.map((type) => (
          <button
            key={type}
            type="button"
            data-entry-type={type}
            onClick={() => onAdd(type)}
            disabled={disabled}
            className={getButtonClasses(isMobile, `flex items-center justify-center ${isMobile ? 'py-3 px-3 flex-1 min-h-[48px]' : 'px-3 py-2'} rounded-lg border ${
              disabled ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed' : QUICK_ADD_TONES[type] || QUICK_ADD_TONES.notes
            }`)}
          >
            <span className="relative mr-1 flex">
              <EntryIcon type={type} />
              <Plus size={isMobile ? 10 : 12} className="absolute -top-1 right-1 rounded-full bg-white p-0.5" />
            </span>
            <span className="text-sm font-medium">{QUICK_ADD_LABELS[type] || noteTemplateFor(type)?.label || type}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuickAddMenu;
